/**
 * Resolves the axios base URL for both web and native (Capacitor) runtimes.
 *
 * Web (browser):   relative '/api'. In dev, CRA proxies to the backend
 *                  container; in prod, the backend serves the SPA from the
 *                  same origin, so '/api' is correct.
 *
 * Native (Capacitor): the webview origin is http(s)://localhost, so a
 *                  relative '/api' would hit the bundled app, not the
 *                  backend. We must use an ABSOLUTE origin. It is read from
 *                  REACT_APP_API_URL, baked in at build time. Build the
 *                  native bundle with, e.g.:
 *                    REACT_APP_API_URL=https://app.marakin.co.uk npm run build
 *                  (no trailing slash; '/api' is appended here).
 *
 * On web, REACT_APP_API_URL is honoured ONLY when the page itself runs on
 * localhost (pointing a local web build at a deployed backend). Deployed web
 * always uses same-origin '/api': .env.production bakes the railway.app URL
 * into the Railway web build too, so on app.marakin.co.uk every call went
 * cross-origin and the production CSP (connect-src 'self') blocked them all
 * (found 15 Oct 2026 when the B-53 sign-in buttons never appeared).
 */
import { Capacitor } from '@capacitor/core';

const ABSOLUTE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

export function resolveApiBaseUrl() {
  if (Capacitor.isNativePlatform()) {
    if (!ABSOLUTE) {
      // Fail loud in dev console — a native build without this is unusable.
      // eslint-disable-next-line no-console
      console.error(
        '[Marakin] REACT_APP_API_URL is not set. The native app cannot reach ' +
        'the backend. Rebuild with REACT_APP_API_URL=https://<prod-api> npm run build'
      );
    }
    return `${ABSOLUTE}/api`;
  }
  // Web: same-origin '/api', unless a localhost page is pointed at a deployed backend.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalPage = host === 'localhost' || host === '127.0.0.1';
  return ABSOLUTE && isLocalPage ? `${ABSOLUTE}/api` : '/api';
}

export const API_BASE_URL = resolveApiBaseUrl();

/**
 * Public web origin for building SHAREABLE links (invite links, QR codes…).
 *
 * Never use window.location.origin for share links: inside the native
 * webview that origin is 'localhost', so the copied link is dead on every
 * other device (tester feedback F-010). REACT_APP_API_URL is the public
 * origin (the backend serves the SPA from the same origin in prod), so we
 * prefer it everywhere and only fall back to window.location.origin on web,
 * where it is genuinely the public origin.
 */
export function resolvePublicWebUrl() {
  // W-14 (11 Sept): on WEB, window.location.origin is ALWAYS the public
  // origin — preferring ABSOLUTE here made local share links point at the
  // API container (localhost:4000 → "Route not found"), and was only right
  // on prod because API and SPA happen to share an origin there. The F-010
  // rule is about the NATIVE webview, where origin is localhost — only
  // there must ABSOLUTE win.
  if (!Capacitor.isNativePlatform()) return window.location.origin;
  if (ABSOLUTE) return ABSOLUTE;
  // eslint-disable-next-line no-console
  console.error(
    '[Marakin] REACT_APP_API_URL is not set. Share links generated in the ' +
    'native app would point at localhost and be dead on other devices.'
  );
  return window.location.origin;
}

export const PUBLIC_WEB_URL = resolvePublicWebUrl();
